import axios from 'axios'
import { getAccessTokenPromise } from '..'

// Mill Invite API (auth-backend v1.1.0). Replaces the palmoil_invitation Hasura
// mutations: the invitationEvent trigger and its webhook handler were removed
// server-side, so those mutations no longer send any email.
const base = () => `${process.env.REACT_APP_BASE_URL_PUBLIC}/auth/millInvite`

// `token` overrides getAccessTokenPromise() for callers holding a fresher token
// than redux does (ConfirmAccount disables the global auth listener).
async function authHeaders(token?: string) {
    const t = token ?? await getAccessTokenPromise()
    return {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${t}`,
    }
}

export type CreateMillInviteInput = {
    email: string
    inviterOrganisationId: number
    firstname?: string
    lastname?: string
    isPempemUser?: boolean
}

// Idempotent per (email, inviterOrganisationId): a repeat call updates the names
// and resends the email with a fresh oob, so re-invite is just another POST.
export async function createMillInvite(input: CreateMillInviteInput): Promise<void> {
    // The endpoint rejects unknown properties, so send only what is set.
    const body: CreateMillInviteInput = {
        email: input.email,
        inviterOrganisationId: input.inviterOrganisationId,
    }
    if (input.firstname) { body.firstname = input.firstname }
    if (input.lastname) { body.lastname = input.lastname }
    if (input.isPempemUser !== undefined) { body.isPempemUser = input.isPempemUser }

    await axios.post(base(), body, { withCredentials: false, headers: await authHeaders() })
}

// Returns the number of rows deleted: 0 means there was no matching invitation.
export async function revokeMillInvite(email: string, inviterOrganisationId: number): Promise<number> {
    const res = await axios.delete(base(), {
        withCredentials: false,
        params: { email, inviterOrganisationId },
        headers: await authHeaders(),
    })
    return res.data?.affected ?? 0
}

// Self-scoped: the invitation is looked up by the token's email plus orgId + oob.
export async function confirmMillInvite(inviterOrganisationId: number, oob: string, token?: string): Promise<void> {
    await axios.post(`${base()}/confirm`, { inviterOrganisationId, oob },
        { withCredentials: false, headers: await authHeaders(token) })
}

// The API returns { error: "..." }; showErrorMessage() JSON.stringifies a raw
// axios error down to "{}", so unwrap the message before handing it over.
export function millInviteError(e: any): string {
    return e?.response?.data?.error ?? e?.message ?? 'request failed'
}
